import { useEffect } from "react";

/**
 * Marketing pages follow the visitor's OS theme.
 *
 * The app itself is pinned to light (next-themes runs with enableSystem off),
 * which is right for the product but wrong for a public page: someone browsing
 * in dark mode should get a dark page. So on the marketing pages only, we stamp
 * `data-mk-theme` on <html> from the OS preference and let marketing.css key
 * off it.
 *
 * An explicit choice always wins: if the visitor has previously picked a theme
 * (next-themes stores it under "theme"), we leave them alone.
 */
export function useMarketingTheme() {
  useEffect(() => {
    let hasExplicitChoice = false;
    try {
      const stored = window.localStorage.getItem("theme");
      hasExplicitChoice = stored === "light" || stored === "dark";
    } catch {
      // Private browsing or blocked storage: fall back to the OS preference.
      hasExplicitChoice = false;
    }
    if (hasExplicitChoice) return;

    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      document.documentElement.setAttribute("data-mk-theme", query.matches ? "dark" : "light");
    };

    apply();
    query.addEventListener("change", apply);

    return () => {
      query.removeEventListener("change", apply);
      document.documentElement.removeAttribute("data-mk-theme");
    };
  }, []);
}
